import type { Task } from '@medplum/fhirtypes';
import type { AppointmentRequestDetailsPayload } from '../pages/messaging/appointmentRequestMessage';
import {
  APPOINTMENT_REQUEST_TASK,
  APPOINTMENT_REQUEST_TASK_INPUT_TYPE,
} from '../pages/client-portal/utils/appointmentRequestTask';
import {
  APPOINTMENT_RESCHEDULE_TASK,
} from '../pages/client-portal/utils/rescheduleRequestTask';
import {
  formatAppointmentRequestTaskDescriptionForClient,
  formatAppointmentRequestTaskDescriptionForProvider,
  formatRescheduleRequestTaskDescriptionForProvider,
} from './portalAppointmentTaskLabels';

export type PortalAppointmentTaskAudience = 'client' | 'provider';

export {
  formatAppointmentRequestTaskDescriptionForClient,
  formatAppointmentRequestTaskDescriptionForProvider,
} from './portalAppointmentTaskLabels';

const LEGACY_EPISODE_DESC = /^Appointment request for episode /i;
const GENERIC_APPOINTMENT_REQUEST_DESC = /^appointment\s+request\s*$/i;
const GENERIC_RESCHEDULE_REQUEST_DESC = /^reschedule\s+request\s*$/i;
/** Legacy stored descriptions: "Appointment request · Name" or "Appointment Request - Name" */
const NAMED_PORTAL_TASK_DESC =
  /^(?:Appointment Request|Reschedule Request|Appointment request|Reschedule request)\s*(?:[·•]|\-)\s*(.+)$/i;
const PORTAL_TASK_TITLE_PREFIX = /^(?:Appointment Request|Appointment request|Reschedule Request|Reschedule request)\b/i;

function taskTypeCode(task: Task): string | undefined {
  return task.code?.coding?.find((c) => c.code)?.code;
}

function codingDisplays(task: Task): string[] {
  return (
    task.code?.coding
      ?.map((c) => c.display?.trim().toLowerCase())
      .filter((d): d is string => Boolean(d)) ?? []
  );
}

function isAppointmentRequestTask(task: Task): boolean {
  if (taskTypeCode(task) === APPOINTMENT_REQUEST_TASK.code) return true;
  if (codingDisplays(task).includes(APPOINTMENT_REQUEST_TASK.display.toLowerCase())) return true;
  const desc = task.description?.trim() ?? '';
  if (LEGACY_EPISODE_DESC.test(desc)) return true;
  if (GENERIC_APPOINTMENT_REQUEST_DESC.test(desc)) return true;
  if (/^appointment\s+request\b/i.test(desc)) return true;
  return false;
}

function isRescheduleRequestTask(task: Task): boolean {
  if (taskTypeCode(task) === APPOINTMENT_RESCHEDULE_TASK.code) return true;
  if (codingDisplays(task).includes(APPOINTMENT_RESCHEDULE_TASK.display.toLowerCase())) return true;
  if (codingDisplays(task).includes('reschedule request')) return true;
  const desc = task.description?.trim() ?? '';
  if (GENERIC_RESCHEDULE_REQUEST_DESC.test(desc)) return true;
  if (/^reschedule\s+request\b/i.test(desc)) return true;
  return false;
}

function parseTaskInputPayload(
  task: Task,
  inputType: string,
): AppointmentRequestDetailsPayload | null {
  const raw = task.input?.find((item) => item.type?.text === inputType)?.valueString;
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AppointmentRequestDetailsPayload;
  } catch {
    return null;
  }
}

function clientNameFromDescriptionSuffix(desc: string): string | undefined {
  const named = desc.match(NAMED_PORTAL_TASK_DESC);
  return named?.[1]?.trim() || undefined;
}

/** Client display name for provider portal appointment/reschedule Tasks. */
export function resolveProviderPortalTaskClientName(task: Task): string | undefined {
  const fromFor = task.for?.display?.trim();
  if (fromFor && !PORTAL_TASK_TITLE_PREFIX.test(fromFor)) return fromFor;

  const desc = task.description?.trim() ?? '';
  return clientNameFromDescriptionSuffix(desc);
}

/** Human-readable Task.description for portal appointment/reschedule Tasks. */
export function getPortalAppointmentTaskDescription(
  task: Task,
  audience: PortalAppointmentTaskAudience,
): string | undefined {
  if (isAppointmentRequestTask(task)) {
    const details = parseTaskInputPayload(task, APPOINTMENT_REQUEST_TASK_INPUT_TYPE);
    if (audience === 'client') {
      return formatAppointmentRequestTaskDescriptionForClient(details?.serviceType);
    }
    return formatAppointmentRequestTaskDescriptionForProvider(
      resolveProviderPortalTaskClientName(task),
    );
  }

  if (isRescheduleRequestTask(task)) {
    if (audience === 'client') {
      return 'Reschedule Request';
    }
    return formatRescheduleRequestTaskDescriptionForProvider(
      resolveProviderPortalTaskClientName(task),
    );
  }

  return undefined;
}

export function getTaskDisplayDescription(
  task: Task,
  audience: PortalAppointmentTaskAudience,
): string {
  const portal = getPortalAppointmentTaskDescription(task, audience);
  if (portal) return portal;

  const desc = task.description?.trim() ?? '';
  if (/^appointment\s+request\b/i.test(desc)) {
    if (audience === 'client') {
      const details = parseTaskInputPayload(task, APPOINTMENT_REQUEST_TASK_INPUT_TYPE);
      return formatAppointmentRequestTaskDescriptionForClient(
        details?.serviceType ?? clientNameFromDescriptionSuffix(desc),
      );
    }
    return formatAppointmentRequestTaskDescriptionForProvider(
      resolveProviderPortalTaskClientName(task) ?? clientNameFromDescriptionSuffix(desc),
    );
  }

  if (/^reschedule\s+request\b/i.test(desc)) {
    if (audience === 'client') return 'Reschedule Request';
    return formatRescheduleRequestTaskDescriptionForProvider(
      resolveProviderPortalTaskClientName(task) ?? clientNameFromDescriptionSuffix(desc),
    );
  }

  if (desc && !GENERIC_APPOINTMENT_REQUEST_DESC.test(desc) && !GENERIC_RESCHEDULE_REQUEST_DESC.test(desc)) {
    return desc;
  }

  return '';
}
